// 절차 효과음 엔진(c019): 레시피 은행(시드 변형을 미리 렌더한 AudioBuffer) + 미리 만든 목소리 그래프 + 묶음 상한·우선순위 + 큰 타격 덕킹.
// 그래프: 목소리 i = [소스] → Gain → StereoPanner → (dry Gain → 합성 버스) / (duck send Gain → 덕킹 Gain → 합성 버스), 합성 버스 → 효과음 버스(dest).
// 효과음 버스 뒤의 마스터·효과음 볼륨·리미터·음소거는 기존 엔진(index.ts) 그대로 받는다.
// 재생 경로(play)는 렌더하지 않고 JS 객체도 만들지 않는다. WebAudio 규칙상 일회용인 AudioBufferSourceNode 하나만 새로 만든다
// (기존 파일 효과음과 같은 예외). 은행은 렌더를 작은 단계(RecipeJob)로 나눠 유휴 시간(requestIdleCallback) 또는 프레임당 4ms 예산
// 안에서 채운다. 순서는 경고·위험 → 나머지(변형 1을 다 채운 뒤 변형 2). 아직 없는 소리는 다른 변형을 쓰고, 그것도 없으면
// 다음 워밍 맨 앞으로 올리고 이번에는 건너뛴다. 경고·위험 소리만 빠지면 안 되므로 그때는 그 자리에서 렌더한다.
import type { GameEvent } from '../../shared/events';
import type { HazardTheme } from '../../shared/levels';
import { distanceDb, panFor } from '../decide';
import { dbToGain } from '../duck';
import { decideSynth, isHeavy, SynthCueList, type SynthCue } from './decide';
import { GriftSfx } from './griftSfx';
import { RecipeJob } from './dsp';
import { SynthVoicePool } from './pool';
import { CATS, Cat, SYNTH_DEFS, SYNTH_NAMES, SYNTH_SR, VARIANTS, variantSeed } from './recipes';
import { lyraSfxId } from './lyraSfx';

/** 큰 타격 덕킹: 덕킹 묶음(줍기·고유 효과·상태)을 -7dB로 8ms에 내리고 90ms 유지 뒤 시간상수 120ms로 돌린다 */
export const SYNTH_DUCK = { db: -7, attack: 0.008, hold: 0.09, release: 0.12 } as const;
/** 합성 목소리 수 */
export const SYNTH_VOICES = 12;
/** 재생마다 흔드는 폭: 피치(센트)·음량(dB) */
export const PLAY_JITTER = { cents: 22, db: 0.75 } as const;
/** 한 프레임에 모아 두는 소리 최대 수(넘치면 우선순위 낮은 것부터 버린다) */
const PENDING = 16;
/** 워밍 예산: 한 번에 쓰는 최대 시간(ms), 직전 프레임이 이보다 길면(초) 그 프레임은 건너뛴다 */
export const WARM_BUDGET_MS = 4;
export const WARM_SKIP_DT = 0.02;
/** 무거운 프레임만 이어질 때: 이만큼(ms) 워밍을 못 했으면 그 프레임에 WARM_STARVE_BUDGET_MS만 쓴다 */
export const WARM_STARVE_MS = 250;
export const WARM_STARVE_BUDGET_MS = 2;
/** 유휴 콜백 시간 초과(ms) */
export const WARM_IDLE_TIMEOUT = 250;
/** 경고 소리는 거리 감쇠를 절반만(멀리 있는 보스 경고도 들려야 한다) */
const TELE_DIST_SCALE = 0.5;

export interface SynthStats {
  played: number; stolen: number; droppedSpacing: number; droppedCap: number; droppedFull: number;
  gated: number; ducks: number; rendered: number; lateRenders: number; bankTotal: number; byName: Record<string, number>;
  /** 은행에 없어 건너뛴 소리 / 무거운 프레임이라 건너뛴 워밍 / 워밍 단계 수 / 가장 긴 단계(ms) */
  missed: number; warmSkipped: number; warmSteps: number; maxStepMs: number;
}

export class SynthEngine {
  readonly bus: GainNode;
  readonly duckGain: GainNode;
  readonly pool: SynthVoicePool;
  private readonly vGain: GainNode[] = [];
  private readonly vPan: StereoPannerNode[] = [];
  private readonly vDry: GainNode[] = [];
  private readonly vWet: GainNode[] = [];
  private readonly vSrc: (AudioBufferSourceNode | null)[] = [];
  private readonly bank: (AudioBuffer | null)[];
  /** C12 대균열 장면 소리(판이 대균열인지 기억한다) */
  readonly grift = new GriftSfx();
  private readonly scratch: Float32Array<ArrayBuffer>;
  private readonly syncScratch: Float32Array<ArrayBuffer>;
  private readonly job = new RecipeJob();
  private readonly syncJob = new RecipeJob();
  private jobSlot = -1;
  /** 워밍 순서(칸 번호)와 진행 위치, 급한 칸(쌓인 순서의 역순으로 꺼낸다) */
  private readonly order: Int16Array;
  private orderAt = 0;
  private readonly urgent = new Int16Array(8);
  private urgentN = 0;
  private filled = 0;
  private idleOn = false;
  private lastWarmAt = -Infinity;
  private stopped = false;
  private missed = 0;
  private warmSkipped = 0;
  private warmSteps = 0;
  private maxStepMs = 0;
  private readonly lastVariant: Int8Array;
  private readonly counts: Uint32Array;
  readonly cues = new SynthCueList(4);
  /** 이번 프레임에 모인 소리(flush가 우선순위 높은 것부터 낸다 — 클리어 수거 폭주에서 전설이 일반에 밀리지 않게) */
  private readonly pending = new SynthCueList(PENDING);
  private seed: number;
  private gated = 0;
  private ducks = 0;
  private rendered = 0;
  private lateRenders = 0;

  /**
   * gate: 지금 소리를 내도 되는가(첫 입력 해제·탭 보임·마스터·효과음 볼륨 > 0). 거짓이면 아무 노드도 만들지 않는다.
   * seed: 재생 난수 시드(시험 고정용), now: 워밍 예산 시계(ms, 시험 주입용)
   */
  constructor(private readonly ctx: BaseAudioContext, dest: AudioNode, private readonly gate: () => boolean, seed = 0x2f6b9d1d,
    private readonly now: () => number = () => performance.now()) {
    this.seed = seed | 0 || 1;
    this.bus = ctx.createGain();
    this.duckGain = ctx.createGain();
    this.duckGain.connect(this.bus);
    this.bus.connect(dest);
    for (let i = 0; i < SYNTH_VOICES; i++) {
      const g = ctx.createGain(), p = ctx.createStereoPanner(), dry = ctx.createGain(), wet = ctx.createGain();
      g.connect(p); p.connect(dry); p.connect(wet); dry.connect(this.bus); wet.connect(this.duckGain);
      wet.gain.value = 0;
      this.vGain.push(g); this.vPan.push(p); this.vDry.push(dry); this.vWet.push(wet); this.vSrc.push(null);
    }
    this.pool = new SynthVoicePool(SYNTH_VOICES, CATS);
    this.bank = new Array<AudioBuffer | null>(SYNTH_DEFS.length * VARIANTS).fill(null);
    let maxDur = 0;
    for (const d of SYNTH_DEFS) maxDur = Math.max(maxDur, d.recipe.dur);
    this.scratch = new Float32Array(Math.ceil(maxDur * SYNTH_SR) + 1);
    this.syncScratch = new Float32Array(this.scratch.length);
    // 워밍 순서: 경고·위험 변형 1 → 나머지 변형 1 → (같은 순서로) 변형 2…
    const order: number[] = [];
    const urgentCat = (c: number): boolean => c === Cat.Tele || c === Cat.Hazard;
    for (let v = 0; v < VARIANTS; v++) {
      SYNTH_DEFS.forEach((d, id) => { if (urgentCat(d.cat)) order.push(id * VARIANTS + v); });
      SYNTH_DEFS.forEach((d, id) => { if (!urgentCat(d.cat)) order.push(id * VARIANTS + v); });
    }
    this.order = Int16Array.from(order);
    this.lastVariant = new Int8Array(SYNTH_DEFS.length).fill(-1);
    this.counts = new Uint32Array(SYNTH_DEFS.length);
  }

  /** 은행이 다 찼는가 */
  get ready(): boolean { return this.filled >= this.bank.length; }

  /**
   * 렌더 프레임마다: 직전 프레임이 무거웠으면(dt > 20ms) 건너뛰고, 아니면 WARM_BUDGET_MS 안에서 렌더 단계를 잇는다.
   * 유휴 콜백이 있는 브라우저에서도 같이 돈다(유휴가 드문 기기에서 은행이 늦게 차지 않게). 무거운 프레임만 계속돼
   * 250ms 넘게 아무 워밍도 못 했으면 그 프레임에 2ms만 쓴다(굶주림 방지). 다 차면 아무것도 하지 않는다.
   */
  warmStep(frameDt: number): void {
    if (this.ready || this.stopped) return;
    if (frameDt > WARM_SKIP_DT) {
      if (this.now() - this.lastWarmAt < WARM_STARVE_MS) { this.warmSkipped++; return; }
      this.warmFor(WARM_STARVE_BUDGET_MS);
      return;
    }
    this.warmFor(WARM_BUDGET_MS);
  }

  /**
   * 유휴 시간에도 워밍한다(requestIdleCallback이 있으면 true). 프레임 경로(warmStep)와 함께 돈다.
   * 유휴가 250ms 넘게 오지 않으면(프레임이 꽉 찬 기기) 시간 초과 콜백에서 4ms 예산을 쓴다 — 은행이 끝내 찬다.
   */
  startIdleWarm(): boolean {
    const W = (typeof window !== 'undefined' ? window : undefined) as unknown as { requestIdleCallback?: (cb: (d: IdleDeadline) => void, o?: { timeout: number }) => number } | undefined;
    const ric = W?.requestIdleCallback;
    if (typeof ric !== 'function') return false;
    this.idleOn = true;
    const tick = (d: IdleDeadline): void => {
      if (this.stopped || this.ready) { this.idleOn = !this.ready && !this.stopped; return; }
      this.warmFor(d.didTimeout ? WARM_BUDGET_MS : Math.max(1, Math.min(WARM_BUDGET_MS, d.timeRemaining() - 1)));
      if (!this.ready) ric.call(W, tick, { timeout: WARM_IDLE_TIMEOUT });
    };
    ric.call(W, tick, { timeout: WARM_IDLE_TIMEOUT });
    return true;
  }

  /** 워밍을 멈춘다(오류로 합성 층을 끌 때) */
  stop(): void { this.stopped = true; }

  /** budgetMs 안에서 렌더 단계를 이어 간다(최소 한 단계). 단계 하나는 데스크톱 1ms 안팎 */
  warmFor(budgetMs: number): void {
    const t0 = this.now();
    this.lastWarmAt = t0;
    for (;;) {
      if (this.jobSlot < 0) {
        const k = this.nextSlot();
        if (k < 0) return;
        this.beginSlot(this.job, k, this.scratch);
        this.jobSlot = k;
      }
      const ts = this.now();
      const done = this.job.step();
      const dt = this.now() - ts;
      if (dt > this.maxStepMs) this.maxStepMs = dt;
      this.warmSteps++;
      if (done) { this.commitSlot(this.jobSlot, this.job); this.jobSlot = -1; }
      if (this.now() - t0 >= budgetMs) return;
    }
  }

  /** 다음 워밍 칸: 급한 칸(재생이 먼저 찾은 소리) → 정해진 순서(경고·위험 변형 1 → 나머지 변형 1 → 변형 2) */
  private nextSlot(): number {
    while (this.urgentN > 0) {
      const k = this.urgent[--this.urgentN];
      if (!this.bank[k] && k !== this.jobSlot) return k;
    }
    while (this.orderAt < this.order.length) {
      const k = this.order[this.orderAt];
      if (!this.bank[k] && k !== this.jobSlot) return k;
      this.orderAt++;
    }
    return -1;
  }

  /** 칸 k를 다음 워밍의 맨 앞으로 */
  private request(k: number): void {
    for (let i = 0; i < this.urgentN; i++) if (this.urgent[i] === k) return;
    if (this.urgentN < this.urgent.length) this.urgent[this.urgentN++] = k;
  }

  private beginSlot(job: RecipeJob, k: number, scratch: Float32Array): void {
    const id = (k / VARIANTS) | 0, v = k - id * VARIANTS, def = SYNTH_DEFS[id];
    job.begin(def.recipe, SYNTH_SR, variantSeed(id, v), CATS[def.cat].targetDb, scratch);
  }

  private commitSlot(k: number, job: RecipeJob): AudioBuffer {
    const out = job.output;
    const buf = this.ctx.createBuffer(1, out.length, SYNTH_SR);
    buf.copyToChannel(out as Float32Array<ArrayBuffer>, 0);
    this.bank[k] = buf;
    this.filled++;
    this.rendered++;
    return buf;
  }

  /** 경고·위험 소리가 은행에 없을 때만: 별도 작업으로 그 자리에서 렌더(진행 중인 워밍은 건드리지 않는다) */
  private renderNow(k: number): AudioBuffer {
    if (this.jobSlot === k) {
      while (!this.job.step()) { /* 끝까지 */ }
      this.jobSlot = -1;
      this.lateRenders++;
      return this.commitSlot(k, this.job);
    }
    this.beginSlot(this.syncJob, k, this.syncScratch);
    while (!this.syncJob.step()) { /* 끝까지 */ }
    this.lateRenders++;
    return this.commitSlot(k, this.syncJob);
  }

  /** 0..1 재생 난수(xorshift32, 할당 없음) */
  private rand(): number {
    let s = this.seed;
    s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
    this.seed = s;
    return (s >>> 0) / 4294967296;
  }

  /** 게임 이벤트 하나: 큰 타격이면 덕킹, 절차 효과음이 있으면 이번 프레임 대기열에 넣는다(flush가 낸다) */
  /** hasSfxFile: 그 번호의 파일 효과음이 하나라도 로드됐는지(없을 때만 합성 대체음을 내는 소리용) */
  onEvent(e: GameEvent, theme: HazardTheme | undefined, hasSfxFile?: (id: number) => boolean): void {
    if (isHeavy(e)) this.duck();
    decideSynth(e, this.cues, theme, hasSfxFile);
    this.grift.decide(e, this.cues);
    for (let i = 0; i < this.cues.count; i++) this.enqueue(this.cues.items[i]);
    this.cues.clear();
  }

  /**
   * 이름으로 부르는 효과음(c022 Lyra 활·화살 큐, lyraSfx.ts). 이벤트 대신 렌더·오디오 연결 쪽이 큐 이름을 알 때 쓴다.
   * x,z = 소리 위치(위치 소리만 팬·거리 감쇠). 같은 프레임 flush에서 우선순위대로 낸다. 반환: 이름이 표에 있으면 true
   */
  cue(name: string, x = 0, z = 0, gainDb = 0, rate = 1): boolean {
    const id = lyraSfxId(name);
    if (id < 0) return false;
    const c = this.cues.add(id, gainDb, rate, x, z);
    if (c) { this.enqueue(c); this.cues.clear(); }
    return true;
  }

  private enqueue(c: SynthCue): void {
    const P = this.pending, prio = SYNTH_DEFS[c.id].prio;
    let slot: SynthCue | null = P.add(c.id, c.gainDb, c.rate, c.x, c.z);
    if (!slot) {
      // 가득 참: 가장 낮은 우선순위(같으면 나중 것)를 새 소리가 더 높을 때만 바꾼다
      let lo = -1;
      for (let i = 0; i < P.count; i++) if (lo < 0 || SYNTH_DEFS[P.items[i].id].prio <= SYNTH_DEFS[P.items[lo].id].prio) lo = i;
      if (lo < 0 || SYNTH_DEFS[P.items[lo].id].prio >= prio) return;
      slot = P.items[lo];
      slot.id = c.id; slot.gainDb = c.gainDb; slot.rate = c.rate; slot.x = c.x; slot.z = c.z;
    }
    slot.positional = c.positional;
  }

  /** 프레임 끝: 모인 소리를 우선순위 높은 것부터(같으면 들어온 순서) 낸다. 삽입 정렬, 할당 없음 */
  flush(heroX: number, heroZ: number): void {
    const P = this.pending, it = P.items;
    for (let i = 1; i < P.count; i++) {
      const c = it[i], p = SYNTH_DEFS[c.id].prio;
      let j = i - 1;
      while (j >= 0 && SYNTH_DEFS[it[j].id].prio < p) { it[j + 1] = it[j]; j--; }
      it[j + 1] = c;
    }
    for (let i = 0; i < P.count; i++) this.play(it[i], heroX, heroZ);
    P.clear();
  }

  /** 큰 타격 덕킹(덕킹 묶음만) */
  duck(): void {
    if (!this.gate()) return;
    const t = this.ctx.currentTime, g = this.duckGain.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.setTargetAtTime(dbToGain(SYNTH_DUCK.db), t, SYNTH_DUCK.attack);
    g.setTargetAtTime(1, t + SYNTH_DUCK.hold, SYNTH_DUCK.release);
    this.ducks++;
  }

  /** 소리 하나. 반환: 실제로 시작(예약)했으면 true */
  play(c: SynthCue, heroX: number, heroZ: number): boolean {
    if (!this.gate()) { this.gated++; return false; }
    const def = SYNTH_DEFS[c.id];
    let v = VARIANTS > 1 ? (this.rand() * VARIANTS) | 0 : 0;
    if (v >= VARIANTS) v = VARIANTS - 1;
    if (VARIANTS > 1 && v === this.lastVariant[c.id]) v = (v + 1) % VARIANTS;
    let buf = this.bank[c.id * VARIANTS + v];
    if (!buf) {
      // 이 변형이 아직 없으면 은행에 있는 다른 변형
      for (let o = 0; o < VARIANTS && !buf; o++) { const b = this.bank[c.id * VARIANTS + o]; if (b) { buf = b; v = o; } }
    }
    if (!buf) {
      if (def.cat === Cat.Tele || def.cat === Cat.Hazard) buf = this.renderNow(c.id * VARIANTS + v);
      else { this.request(c.id * VARIANTS + v); this.missed++; return false; }
    }
    // 피치 흔들림: 2^(센트/1200)의 2차 근사(±22센트 안 오차 1e-6)
    const x = (PLAY_JITTER.cents / 1200) * Math.LN2 * (this.rand() * 2 - 1);
    const rate = c.rate * (1 + x + 0.5 * x * x);
    const t = this.ctx.currentTime;
    const vi = this.pool.acquire(def.cat, def.prio, t, buf.duration / rate);
    if (vi < 0) return false;
    this.lastVariant[c.id] = v;
    const at = this.pool.startAt;
    const old = this.vSrc[vi];
    if (old) { try { old.stop(at); } catch { /* 이미 멈춤 */ } }
    let db = def.gainDb + c.gainDb + PLAY_JITTER.db * (this.rand() * 2 - 1);
    let pan = 0;
    if (c.positional) {
      const dx = c.x - heroX, dz = c.z - heroZ;
      pan = panFor(dx);
      db += distanceDb(Math.sqrt(dx * dx + dz * dz)) * (def.cat === Cat.Tele ? TELE_DIST_SCALE : 1);
    }
    const duck = CATS[def.cat].duck;
    const g = this.vGain[vi].gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(dbToGain(db), at);
    this.vPan[vi].pan.setValueAtTime(pan, at);
    this.vDry[vi].gain.setValueAtTime(duck ? 0 : 1, at);
    this.vWet[vi].gain.setValueAtTime(duck ? 1 : 0, at);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    src.connect(this.vGain[vi]);
    src.start(at);
    this.vSrc[vi] = src;
    this.counts[c.id]++;
    return true;
  }

  /** 모두 멈춘다(탭 가림·음소거). 은행은 남긴다 */
  silence(): void {
    for (let i = 0; i < this.vSrc.length; i++) {
      const s = this.vSrc[i];
      if (s) { try { s.stop(); } catch { /* 이미 멈춤 */ } this.vSrc[i] = null; }
    }
    this.pool.clear();
  }

  stats(): SynthStats {
    const byName: Record<string, number> = {};
    SYNTH_NAMES.forEach((n, i) => { if (this.counts[i] > 0) byName[n] = this.counts[i]; });
    const p = this.pool;
    return {
      played: p.played, stolen: p.stolen, droppedSpacing: p.droppedSpacing, droppedCap: p.droppedCap, droppedFull: p.droppedFull,
      gated: this.gated, ducks: this.ducks, rendered: this.rendered, lateRenders: this.lateRenders, bankTotal: this.bank.length, byName,
      missed: this.missed, warmSkipped: this.warmSkipped, warmSteps: this.warmSteps, maxStepMs: Math.round(this.maxStepMs * 100) / 100,
    };
  }
}
